import { useState, useCallback, useRef, useEffect } from 'react';
import { authFetch } from '../utils/auth';

/**
 * useDraftSave - Hook for managing søknad draft persistence
 *
 * Handles:
 * - Loading the draft (drafts are created on the overview, /soknad/:type)
 * - Auto-saving stage data with debounce
 * - Tracking save status (saving, saved, error)
 *
 * @param {string} stonadType - The stønad type (e.g., 'hjelpestonad')
 * @param {string} requestedDraftId - The draft to load (/soknad/:type/:draftId)
 * @returns {object} Draft management functions and state
 */
export function useDraftSave(stonadType, requestedDraftId) {
  const [draftId, setDraftId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  // requestedDraftId is not one of this user's drafts (deleted, someone
  // else's, or not an id — the backend answers 404 for all three).
  const [notFound, setNotFound] = useState(false);
  const [initialStatus, setInitialStatus] = useState(null);
  // A save is on its way to the server. Two can overlap (a debounced save and
  // a step change), so it stays true until the last one has answered.
  const [isSaving, setIsSaving] = useState(false);
  const savesInFlightRef = useRef(0);
  // A change is waiting for its debounce: not sent yet.
  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [initialStageData, setInitialStageData] = useState(null);
  const [initialStepIndex, setInitialStepIndex] = useState(0);
  // Subject of the søknad (PR: person-picker) — the picked person, restored
  // from the draft so a reload/resume keeps WHO the application is about.
  const [initialSubject, setInitialSubject] = useState(null);

  // Debounce timer ref
  const saveTimerRef = useRef(null);

  // Pending save data ref (for debouncing)
  const pendingSaveRef = useRef(null);

  // Separate debounce machinery for subject saves (PR: person-picker), so the
  // Tier-2 confirm step's per-keystroke updates collapse to a single PUT rather
  // than one network round-trip per character (matters on mobile / flaky
  // connections — the primary target user).
  const subjectTimerRef = useRef(null);
  const pendingSubjectRef = useRef(null);

  /**
   * Load the draft. A 404 sets notFound. Loading never writes: opening a
   * draft leaves its status and updatedAt as they are (the backend moves a
   * klar draft back to in_progress when a changed answer is saved).
   */
  const initializeDraft = useCallback(async () => {
    if (!stonadType) return;

    setIsLoading(true);
    setSaveError(null);
    setNotFound(false);

    try {
      let draftData;
      try {
        const draftResponse = await authFetch(`/soknad/${stonadType}/drafts/${requestedDraftId}`);
        draftData = await draftResponse.json();
      } catch (error) {
        if (error?.status === 404) {
          setNotFound(true);
          return;
        }
        throw error;
      }

      setDraftId(requestedDraftId);
      setInitialStatus(draftData.status);
      setInitialStageData(draftData.stages || {});
      setInitialSubject({
        subjectEntityId: draftData.subjectEntityId ?? null,
        subjectName: draftData.subjectName ?? null,
        // Default true mirrors the schema default → Tier-1 behavior for any
        // draft created before the picker existed.
        subjectIsOnboardedChild: draftData.subjectIsOnboardedChild ?? true,
        subjectIdentity: draftData.subjectIdentity ?? null,
      });

      // The saved step, for resuming (the wizard opens a klar draft on its summary)
      setInitialStepIndex(draftData.currentStepIndex || 0);
    } catch (error) {
      console.error('[useDraftSave] Error initializing draft:', error);
      setSaveError('Kunne ikke laste kladden. Prøv igjen.');
    } finally {
      setIsLoading(false);
    }
  }, [stonadType, requestedDraftId]);

  const startSave = useCallback(() => {
    savesInFlightRef.current += 1;
    setIsSaving(true);
    setSaveError(null);
  }, []);

  const finishSave = useCallback(() => {
    savesInFlightRef.current -= 1;
    if (savesInFlightRef.current === 0) setIsSaving(false);
  }, []);

  const syncPendingChanges = useCallback(() => {
    setHasPendingChanges(Boolean(pendingSaveRef.current || pendingSubjectRef.current));
  }, []);

  /**
   * Execute the actual save to the backend. keepalive lets a save sent as the
   * page goes away outlive it.
   */
  const executeSave = useCallback(async (stageName, stageData, currentStepIndex, { keepalive = false } = {}) => {
    if (!draftId || !stageName) return;

    startSave();

    try {
      await authFetch(`/soknad/${stonadType}/drafts/${draftId}/stages/${stageName}`, {
        method: 'PUT',
        keepalive,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...stageData,
          currentStepIndex
        })
      });

      setLastSavedAt(new Date());
    } catch (error) {
      console.error('[useDraftSave] Error saving stage:', error);
      setSaveError('Kunne ikke lagre. Prøver igjen...');
    } finally {
      finishSave();
    }
  }, [draftId, stonadType, startSave, finishSave]);

  /**
   * Save stage data with debounce (for auto-save on input changes)
   */
  const saveStageDebounced = useCallback((stageName, stageData, currentStepIndex, debounceMs = 1000) => {
    // Store the pending save
    pendingSaveRef.current = { stageName, stageData, currentStepIndex };
    setHasPendingChanges(true);

    // Clear existing timer
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }

    // Set new timer
    saveTimerRef.current = setTimeout(() => {
      const pending = pendingSaveRef.current;
      if (pending) {
        executeSave(pending.stageName, pending.stageData, pending.currentStepIndex);
        pendingSaveRef.current = null;
        syncPendingChanges();
      }
    }, debounceMs);
  }, [executeSave, syncPendingChanges]);

  /**
   * Save stage data immediately (for navigation)
   */
  const saveStageNow = useCallback(async (stageName, stageData, currentStepIndex) => {
    // Clear any pending debounced save
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    pendingSaveRef.current = null;
    syncPendingChanges();

    await executeSave(stageName, stageData, currentStepIndex);
  }, [executeSave, syncPendingChanges]);

  /**
   * Persist the picked subject (PR: person-picker) to the draft via the
   * draft-level PUT. `fields` is any subset of
   * { subjectEntityId, subjectName, subjectIsOnboardedChild, subjectIdentity };
   * the backend updates only provided keys.
   */
  const executeSubjectSave = useCallback(async (fields, { keepalive = false } = {}) => {
    if (!draftId) return;

    startSave();

    try {
      await authFetch(`/soknad/${stonadType}/drafts/${draftId}`, {
        method: 'PUT',
        keepalive,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(fields),
      });
      setLastSavedAt(new Date());
    } catch (error) {
      console.error('[useDraftSave] Error saving subject:', error);
      setSaveError('Kunne ikke lagre. Prøver igjen...');
    } finally {
      finishSave();
    }
  }, [draftId, stonadType, startSave, finishSave]);

  // Debounced subject save — for the Tier-2 confirm step's per-keystroke
  // updates. Each call carries the FULL subjectIdentity snapshot, so collapsing
  // rapid keystrokes to the last one is safe and last-write-wins is preserved.
  const saveSubjectDebounced = useCallback((fields, debounceMs = 800) => {
    pendingSubjectRef.current = fields;
    setHasPendingChanges(true);
    if (subjectTimerRef.current) {
      clearTimeout(subjectTimerRef.current);
    }
    subjectTimerRef.current = setTimeout(() => {
      const pending = pendingSubjectRef.current;
      if (pending) {
        executeSubjectSave(pending);
        pendingSubjectRef.current = null;
        syncPendingChanges();
      }
    }, debounceMs);
  }, [executeSubjectSave, syncPendingChanges]);

  /**
   * Send every change still waiting for its debounce now: when the parent
   * clicks "Neste" within the debounce window, and when they leave the page
   * (see the effect below), so no last change is lost.
   */
  const flushPendingSaves = useCallback(async () => {
    clearTimeout(saveTimerRef.current);
    clearTimeout(subjectTimerRef.current);
    saveTimerRef.current = null;
    subjectTimerRef.current = null;
    const stage = pendingSaveRef.current;
    const subject = pendingSubjectRef.current;
    pendingSaveRef.current = null;
    pendingSubjectRef.current = null;
    syncPendingChanges();

    await Promise.all([
      stage && executeSave(stage.stageName, stage.stageData, stage.currentStepIndex, { keepalive: true }),
      subject && executeSubjectSave(subject, { keepalive: true }),
    ]);
  }, [executeSave, executeSubjectSave, syncPendingChanges]);

  /**
   * Update draft status
   */
  const updateDraftStatus = useCallback(async (status) => {
    if (!draftId) return;

    try {
      await authFetch(`/soknad/${stonadType}/drafts/${draftId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });

    } catch (error) {
      console.error('[useDraftSave] Error updating draft status:', error);
      throw error;
    }
  }, [draftId, stonadType]);

  /**
   * Delete current draft
   */
  const deleteDraft = useCallback(async () => {
    if (!draftId) return;

    try {
      await authFetch(`/soknad/${stonadType}/drafts/${draftId}`, {
        method: 'DELETE'
      });

      setDraftId(null);
      setInitialStageData(null);
    } catch (error) {
      console.error('[useDraftSave] Error deleting draft:', error);
      throw error;
    }
  }, [draftId, stonadType]);

  // Load the draft on mount. The route remounts the page for another draft id.
  useEffect(() => {
    initializeDraft();
  }, [initializeDraft]);

  // Leaving the page sends what is still waiting: the wizard unmounting (a
  // link, the sidebar, the browser's Back, another draft) and the page being
  // hidden or unloaded (app switch, tab closed or reloaded; beforeunload is
  // unreliable on mobile).
  const flushPendingSavesRef = useRef(flushPendingSaves);
  useEffect(() => {
    flushPendingSavesRef.current = flushPendingSaves;
  }, [flushPendingSaves]);

  useEffect(() => {
    const flush = () => flushPendingSavesRef.current();
    const flushIfHidden = () => {
      if (document.visibilityState === 'hidden') flush();
    };
    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', flushIfHidden);
    return () => {
      window.removeEventListener('pagehide', flush);
      document.removeEventListener('visibilitychange', flushIfHidden);
      flush();
    };
  }, []);

  return {
    // State
    draftId,
    isLoading,
    notFound,
    initialStatus,
    isSaving,
    hasPendingChanges,
    saveError,
    lastSavedAt,
    initialStageData,
    initialStepIndex,
    initialSubject,

    // Actions
    saveStageDebounced,
    saveStageNow,
    saveSubjectDebounced,
    flushPendingSaves,
    updateDraftStatus,
    deleteDraft,
    reinitialize: initializeDraft
  };
}

export default useDraftSave;
